"use client";

import { useState } from "react";
import { CheckCircle2, Calendar, Users, Loader2 } from "lucide-react";
import styles from "./BookingEngineSection.module.css";
import Image from "next/image";

interface Room {
  id: string;
  name: string;
  price: string;
  image: string;
  badge?: string;
}

export default function BookingEngineSection() {
  const [checkIn, setCheckIn] = useState("2026-05-21");
  const [checkOut, setCheckOut] = useState("2026-05-23");
  const [guests, setGuests] = useState("2 Adults");
  const [isSearching, setIsSearching] = useState(false);
  const [searchDone, setSearchDone] = useState(false);

  const rooms: Room[] = [
    {
      id: "deluxe",
      name: "Deluxe Room",
      price: "₹4,500",
      image: "/images/rooms/deluxe.jpg",
      badge: "Popular",
    },
    {
      id: "executive",
      name: "Executive Suite",
      price: "₹7,200",
      image: "/images/rooms/executive.jpg",
      badge: "Best Seller",
    },
    {
      id: "premium",
      name: "Premium Room",
      price: "₹9,800",
      image: "/images/rooms/premium.jpg",
    },
  ];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSearching(true);
    setSearchDone(false);
    setTimeout(() => {
      setIsSearching(false);
      setSearchDone(true);
    }, 1200);
  };

  return (
    <section className={styles.booking} id="booking-engine">
      <div className={`${styles.container} container`}>
        <div className={styles.grid}>
          {/* Left Column: Content */}
          <div className={styles.content}>
            <div className={styles.tag}>
              <span className="badge badge-green">Direct Booking Engine</span>
            </div>
            
            <h2 className={styles.title}>
              Accept Direct Bookings <br />
              &amp; Increase Revenue
            </h2>
            
            <p className={styles.description}>
              Enable guests to check availability, book rooms and pay online directly from your website.
            </p>

            <div className={styles.features}>
              <div className={styles.featureItem}>
                <CheckCircle2 size={16} className={styles.checkIcon} />
                <span>Real-time Availability</span>
              </div>
              <div className={styles.featureItem}>
                <CheckCircle2 size={16} className={styles.checkIcon} />
                <span>Booking Notifications</span>
              </div>
              <div className={styles.featureItem}>
                <CheckCircle2 size={16} className={styles.checkIcon} />
                <span>Instant Confirmation</span>
              </div>
              <div className={styles.featureItem}>
                <CheckCircle2 size={16} className={styles.checkIcon} />
                <span>Reduce OTA Commission</span>
              </div>
              <div className={styles.featureItem}>
                <CheckCircle2 size={16} className={styles.checkIcon} />
                <span>Secure Online Payments</span>
              </div>
              <div className={styles.featureItem}>
                <CheckCircle2 size={16} className={styles.checkIcon} />
                <span>Boost Direct Bookings</span>
              </div>
            </div>

            <a href="#demo" className={styles.btnAction}>
              See Booking Engine in Action
            </a>
          </div>

          {/* Right Column: Interactive Booking Widget */}
          <div className={styles.visual}>
            <div className={styles.widgetCard}>
              <h3 className={styles.widgetTitle}>Check Availability</h3>
              
              {/* Search Form */}
              <form onSubmit={handleSearch} className={styles.searchForm}>
                <div className={styles.inputWrapper}>
                  <label className={styles.label}>
                    <Calendar size={12} /> Check In
                  </label>
                  <input
                    type="date"
                    value={checkIn}
                    onChange={(e) => setCheckIn(e.target.value)}
                    className={styles.input}
                    required
                  />
                </div>

                <div className={styles.inputWrapper}>
                  <label className={styles.label}>
                    <Calendar size={12} /> Check Out
                  </label>
                  <input
                    type="date"
                    value={checkOut}
                    onChange={(e) => setCheckOut(e.target.value)}
                    className={styles.input}
                    required
                  />
                </div>

                <div className={styles.inputWrapper}>
                  <label className={styles.label}>
                    <Users size={12} /> Guests
                  </label>
                  <select
                    value={guests}
                    onChange={(e) => setGuests(e.target.value)}
                    className={styles.select}
                  >
                    <option value="1 Adult">1 Adult</option>
                    <option value="2 Adults">2 Adults</option>
                    <option value="3 Adults">3 Adults</option>
                    <option value="4 Adults">4 Adults</option>
                  </select>
                </div>

                <button type="submit" className={styles.btnSearch} disabled={isSearching}>
                  {isSearching ? <Loader2 size={14} className={styles.spinner} /> : "Search Rooms"}
                </button>
              </form>

              {/* Room Cards Row */}
              <div className={`${styles.roomsGrid} ${isSearching ? styles.searchingOpacity : ""}`}>
                {rooms.map((room) => (
                  <div key={room.id} className={styles.roomCard}>
                    <div className={styles.roomImageWrapper}>
                      <Image
                        src={room.image}
                        alt={room.name}
                        fill
                        sizes="(max-width: 768px) 100vw, 150px"
                        style={{ objectFit: "cover" }}
                      />
                      {room.badge && <span className={styles.roomBadge}>{room.badge}</span>}
                    </div>
                    <div className={styles.roomDetails}>
                      <h4 className={styles.roomName}>{room.name}</h4>
                      <div className={styles.roomPriceRow}>
                        <span className={styles.roomPrice}>{room.price}</span>
                        <span className={styles.roomNight}>/ night</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {searchDone && (
                <div className={styles.successToast}>
                  ✓ 3 Rooms available for May 21 - May 23!
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
